import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

export default function StepFinancing({ quote, update }) {
    const fin = quote.financing || {};
    const setFin = (patch) => update({ financing: { ...fin, ...patch } });

    const tl = quote.timeline || {};
    const setTL = (patch) => update({ timeline: { ...tl, ...patch } });

    const deposits = quote.deposits || [];
    const setDep = (idx, patch) => {
        const n = deposits.slice();
        n[idx] = { ...n[idx], ...patch };
        update({ deposits: n });
    };
    const addDep = () => update({ deposits: [...deposits, { label: deposits.length === 0 ? "First refundable good faith deposit" : "Second deposit", amount_required: 0, amount_paid: 0, status: "pending", payment_method: "" }] });
    const removeDep = (idx) => update({ deposits: deposits.filter((_, i) => i !== idx) });

    return (
        <div className="space-y-4" data-testid="step-financing">
            <Card className="rounded-xl border-slate-200 p-5 space-y-4">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Payment & financing</div>
                    <div className="text-xs text-slate-500">Tracks how the customer is paying.</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Purchase type</Label>
                        <Select value={fin.purchase_type || "financing"} onValueChange={(v) => setFin({ purchase_type: v })}>
                            <SelectTrigger data-testid="step-financing-purchase-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="cash">Cash</SelectItem>
                                <SelectItem value="financing">Financing</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Financing status</Label>
                        <Select value={fin.financing_status || "not_applicable"} onValueChange={(v) => setFin({ financing_status: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="not_applicable">Not applicable</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="pre_approved">Pre-approved</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="declined">Declined</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Pre-approval amount</Label>
                        <Input type="number" step="100" value={fin.pre_approval_amount || ""} onChange={(e) => setFin({ pre_approval_amount: parseFloat(e.target.value || 0) })} />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Customer target budget</Label>
                        <Input type="number" step="100" value={fin.target_budget || ""} onChange={(e) => setFin({ target_budget: parseFloat(e.target.value || 0) })} />
                    </div>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2">
                    <div>
                        <div className="text-sm font-medium">EHS loan officer used?</div>
                        <div className="text-xs text-slate-500">Adds $1,000 loan fee to internal calculations.</div>
                    </div>
                    <Switch
                        checked={!!quote.ehs_loan_used}
                        onCheckedChange={(v) => { update({ ehs_loan_used: v }); setFin({ ehs_loan_used: v }); }}
                        data-testid="step-financing-ehs-loan-switch"
                    />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Customer-facing financing notes</Label>
                    <Textarea
                        rows={3}
                        value={fin.notes_customer || ""}
                        onChange={(e) => setFin({ notes_customer: e.target.value })}
                        placeholder="Will appear on the customer-facing quote."
                        data-testid="step-financing-notes-customer"
                    />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Internal financing notes</Label>
                    <Textarea
                        rows={2}
                        value={fin.notes_internal || ""}
                        onChange={(e) => setFin({ notes_internal: e.target.value })}
                        placeholder="Hidden from customer."
                    />
                </div>
            </Card>

            <Card className="rounded-xl border-slate-200 p-5 space-y-4">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Deposits</div>
                    <div className="text-xs text-slate-500">Track refundable deposits as they are paid.</div>
                </div>
                {deposits.length === 0 && (
                    <div className="text-sm text-slate-500 italic">No deposits added yet.</div>
                )}
                {deposits.map((d, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-3">
                        <div className="flex items-center justify-between">
                            <Input value={d.label} onChange={(e) => setDep(i, { label: e.target.value })} className="max-w-md" />
                            <button onClick={() => removeDep(i)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                                <Label className="text-xs text-slate-500">Required</Label>
                                <Input type="number" step="50" value={d.amount_required || 0} onChange={(e) => setDep(i, { amount_required: parseFloat(e.target.value || 0) })} />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Paid</Label>
                                <Input type="number" step="50" value={d.amount_paid || 0} onChange={(e) => setDep(i, { amount_paid: parseFloat(e.target.value || 0) })} />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Status</Label>
                                <Select value={d.status || "pending"} onValueChange={(v) => setDep(i, { status: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="paid">Paid</SelectItem>
                                        <SelectItem value="refunded">Refunded</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-500">Payment method</Label>
                                <Input value={d.payment_method || ""} onChange={(e) => setDep(i, { payment_method: e.target.value })} placeholder="Wire / Zelle / Check" />
                            </div>
                        </div>
                    </div>
                ))}
                <Button variant="outline" size="sm" onClick={addDep} data-testid="step-financing-add-deposit"><Plus className="mr-1 h-4 w-4" /> Add deposit</Button>
            </Card>

            <Card className="rounded-xl border-slate-200 p-5 space-y-3">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Timeline (optional)</div>
                    <div className="text-xs text-slate-500">Internal milestones — not shown to customer.</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                        ["loan_approval", "Loan approval"],
                        ["loan_closing", "Loan closing"],
                        ["permit_approval", "Permit approval"],
                        ["site_ready", "Site ready"],
                        ["delivery", "Delivery"],
                        ["installation", "Installation"],
                        ["move_in", "Move-in"],
                        ["walkthrough", "Walkthrough"],
                    ].map(([k, lbl]) => (
                        <div key={k}>
                            <Label className="text-xs text-slate-500">{lbl}</Label>
                            <Input type="date" value={tl[k] || ""} onChange={(e) => setTL({ [k]: e.target.value })} />
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
}
